import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  Address,
  CampaignTariffSelectionData,
  IConfirmationStatus,
  IContractDetails,
  IdCard,
  IERechnungsInfo,
  INewClientTrackingData,
  IOlavEntry,
  IOrderData,
  IOrderDetailsInfo,
  IOrganisation,
  IPersonalData,
  IProduktAuswahl,
  IProduktDetails,
  IProduktSelektion,
  IProduktVerfuegbarkeit,
  IUploadResult,
  IVertragskonto,
  NewClientContractDetailsViewModel,
  OrderIPDFLinks,
  PreisIndikation,
  Product,
} from '@mwe/models';
import { BehaviorSubject } from 'rxjs';
import { ProductCategoryEnum, ServiceStateEnum } from '@mwe/constants';
import { INewVoucherFormConfig } from '../../voucher/new-voucher/new-voucher.model';
import { withStorageServiceFeature } from '../../store-features/storage-service.feature';
import { withAuthenticationStateFeature } from '../../store-features/authentication.feature';
import { ConfirmationStatusState, withConfirmationStatusState } from '../../store-features/confirmation-status-state.feature';
import { MemoryCacheItem } from '../../cache/memory-cache.service';
import { inject } from '@angular/core';
import { TariffSelectionStateService } from '../../tariff/selection/tariff-selection-state.service';

const STORAGE_KEY = 'internet';

const initialState = {
  tempProductSelectionForVoucher: undefined as IProduktSelektion,
  internetProfileChecked: undefined as boolean,
  newProductAvailabilitiesSuccessStateSubject: undefined as BehaviorSubject<true>,
  submitSuccessStateSubject: undefined as BehaviorSubject<ServiceStateEnum.INIT>,
  internetMgmCode: undefined as string,
  internetAddress: undefined as Address,
  internetAddressOlav: undefined as IOlavEntry,
  invoiceAddressOlav: undefined as IOlavEntry,
  newProductAvailabilities: undefined as IProduktVerfuegbarkeit[],
  newProductAvailabilitiesState: undefined as ServiceStateEnum,
  tariffSelectionState: undefined as ServiceStateEnum,
  tariffSelectionWaerme: undefined as Product,
  predefinedProductsForSelection: undefined as IProduktAuswahl,
  productsForSelection: undefined as IProduktAuswahl,
  productSelection: undefined as IProduktSelektion,
  selectedPriceIndications: undefined as PreisIndikation[],
  registrationDate: undefined as string,
  orderDetails: undefined as IOrderDetailsInfo[],
  personalData: undefined as IPersonalData,
  organisationData: undefined as IOrganisation,
  invoiceData: undefined as IVertragskonto,
  idCard: undefined as IdCard,
  uploadedFiles: undefined as IUploadResult,
  pdfLinks: undefined as OrderIPDFLinks,
  contractDetails: undefined as IContractDetails,
  contractDetailsViewModel: undefined as NewClientContractDetailsViewModel,
  eRechnungsInfo: undefined as IERechnungsInfo,
  notLoggedIn: undefined as string,
  lastOrderDate: undefined as string,
  orderSuccessfull: undefined as boolean,
  submitSuccessState: undefined as ServiceStateEnum,
  trackingData: undefined as INewClientTrackingData,
  sendProductInterestState: undefined as ServiceStateEnum,
  hasPriceIndicationsReloadError: undefined as boolean,
  grantedMarketingPermission: undefined as boolean,
  skipPreselectedAddress: undefined as boolean,
  predefinedOlavKey: undefined as string,
  confirmationStatus: undefined as IConfirmationStatus,
  previousSelectedProductCategories: [] as ProductCategoryEnum[],
  productsToShow: [] as Product[],
  predefinedProducts: [] as Product[],
  selectedProducts: [] as Product[],
  primaryCategorySelected: {} as Record<string, boolean>,
  campaignData: undefined as CampaignTariffSelectionData,
  newVoucherConfig: undefined as INewVoucherFormConfig,
  selectedCategories: undefined as ProductCategoryEnum[],
  initialEmail: undefined as string,
  tkConnIdKey: undefined as string,
};

type InitialInternetState = typeof initialState;

export const InternetStore = signalStore(
  { providedIn: 'root' },
  withConfirmationStatusState({
    _memoryCacheItem: MemoryCacheItem.ClearingAccountDetails,
  }),
  withState(initialState),
  withStorageServiceFeature(STORAGE_KEY, true),
  withAuthenticationStateFeature(),
  withMethods(store => {
    const tariffSelectionStateService = inject(TariffSelectionStateService);
    const updateStoreState = (storeStateValues: Partial<InitialInternetState> & Partial<ConfirmationStatusState>) => {
      patchState(store, { ...storeStateValues });
    };
    const hydrateOrderDetails = (store, orderDetails) => {
      const pdi = { ...orderDetails } as IOrderDetailsInfo;
      const personalData = store.personalData();
      if (!pdi.eRechnungInfo) {
        pdi.eRechnungInfo = store.eRechnungsInfo();
      }
      if (!pdi.produktDetails) {
        pdi.produktDetails = {} as IProduktDetails;
      }
      if (personalData) {
        pdi.produktDetails.partner = {
          partnerId: null,
          partnerGruppe: null,
          nickname: null,
          registrierungStatus: null,
          rolle: null,
          partnerTyp: null,
          anrede: personalData.salutation,
          titel: personalData.title?.text,
          titelKurz: personalData.title?.value,
          nachname: personalData.lastName,
          vorname: personalData.firstName,
          firma: null,
          email: personalData.email,
          telefon: personalData.telefonNumber,
          telefonvorwahl: personalData.telefonCountryCode,
        };
        pdi.produktDetails.rechnungsempfaenger = {
          partnerId: null,
          partnerGruppe: null,
          nickname: null,
          registrierungStatus: null,
          rolle: null,
          partnerTyp: null,
          titel: personalData.title?.value,
          anrede: personalData.salutation,
          nachname: personalData.lastName,
          vorname: personalData.firstName,
          firma: null,
          email: personalData.email,
          telefon: personalData.telefonNumber,
          telefonvorwahl: personalData.telefonCountryCode,
          adresse: {
            ort: store.newClientAddress().city,
            plz: store.newClientAddress().postcode,
            strasse: store.newClientAddress().street,
            hausnummer: store.newClientAddress().streetNumber,
            raum: null,
            land: store.newClientAddress().country,
            adresshinweis: null,
          },
        };
        pdi.partnerDetails = {
          adressen: null,
          bankkonten: null,
          geschaeftsPartnerId: null,
          kontaktdetails: [
            {
              email: { eMail: personalData.email },
              fax: null,
              telefon: { land: personalData.telefonCountryCode, nummer: personalData.telefonNumber },
            },
          ],
          kundensegment: null,
          nickname: null,
          organisation: null,
          systemId: null,
          physischePerson: {
            anrede: personalData.salutation,
            titel: personalData.title?.text,
            titelNachgestellt: null,
            titelVorgestellt: null,
            familienstand: null,
            geschlecht: null,
            staatsbuergerschaft: null,
            vorname: personalData.firstName,
            nachname: personalData.lastName,
            geburtsdatum: personalData.birthday + 'T00:00:00Z',
          },
        };
      }
      if (store.invoiceData()) {
        pdi.produktDetails.vertragskonto = store.invoiceData();
      }
      return pdi;
    };
    const getOrderData = (): IOrderData => {
      const orderDetails = store.orderDetails()?.map(od => hydrateOrderDetails(store, od));
      return {
        selectedOlav: store.internetAddressOlav(),
        newAddress: store.internetAddress(),
        registrationDate: store.registrationDate(),
        orderDetails: orderDetails,
        idCard: store.idCard(),
        uploadedFiles: store.uploadedFiles(),
        pdfLinks: store.pdfLinks(),
        contractDetails: store.contractDetails(),
        priceIndications: tariffSelectionStateService.indications,
      };
    };
    return {
      updateStoreState,
      hydrateOrderDetails,
      getOrderData,
    };
  }),
);
